import { KNOWN_SHAPE_KINDS, isRenderablePinShape, type SnapshotShape } from './shapes';

function isKnownBy(s: SnapshotShape, knownKinds: ReadonlySet<string>): boolean {
  if (!knownKinds.has(s.kind)) return false;
  // A future client may extend the canonical pin record with a style or
  // value this build cannot safely edit. Treat that record exactly like an
  // unknown kind: preserve it verbatim until a compatible client sees it.
  return s.kind !== 'pin' || isRenderablePinShape(s);
}

/**
 * Editor semantics for destructive operations, shared so every surface
 * (web canvas today, extension overlay next) enforces the unknown-kind
 * round-trip rule (shapes.ts) the same way - and so the rule is TESTED
 * here rather than re-implemented inline per consumer.
 */

/**
 * The index of the shape `undo` should remove: the LAST shape on the
 * active image whose kind this build can render. Unknown kinds are
 * skipped - undoing one would be an invisible deletion from the user's
 * perspective. Returns -1 when nothing on this image is undoable.
 */
export function undoTargetIndex(
  shapes: readonly SnapshotShape[],
  activeImageId: string,
  knownKinds: ReadonlySet<string> = KNOWN_SHAPE_KINDS,
): number {
  for (let i = shapes.length - 1; i >= 0; i--) {
    const s = shapes[i];
    if (s !== undefined && s.imageId === activeImageId && isKnownBy(s, knownKinds)) return i;
  }
  return -1;
}

/**
 * Clear-all for one image: removes every KNOWN shape on the active
 * image and preserves everything else - shapes on other images, and
 * unknown-kind shapes on this one. The confirm dialog promises to
 * remove what the user can see; a newer client's shapes are invisible
 * here, and destroying them is the exact cross-version data loss the
 * round-trip rule exists to prevent.
 */
export function clearImageShapes<T extends SnapshotShape>(
  shapes: readonly T[],
  activeImageId: string,
  knownKinds: ReadonlySet<string> = KNOWN_SHAPE_KINDS,
): T[] {
  return shapes.filter((s) => s.imageId !== activeImageId || !isKnownBy(s, knownKinds));
}

/** True when the image has at least one shape that undo/clear can act on. */
export function hasEditableShapes(
  shapes: readonly SnapshotShape[],
  activeImageId: string,
  knownKinds: ReadonlySet<string> = KNOWN_SHAPE_KINDS,
): boolean {
  return shapes.some((s) => s.imageId === activeImageId && isKnownBy(s, knownKinds));
}
